import React, { Suspense, useCallback, useEffect, useState } from "react";
import { Box, Paper, Stack, Table, Title, CloseButton } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { PANEL_TOGGLE_ANIMATION_DURATION } from "config/constants";
import { LEGENDS } from "config/legends";
import { useTranslation } from "react-i18next";
import { updateNonSaveableOptions } from "stores/optionsStore/actions";
import { useOptions } from "stores/optionsStore/OptionsContext";
import { MapID } from "config/enums/mapIDs";

const PANEL_WIDTH = 250;
const LEGEND_SYMBOL_WIDTH = 60;

const useStyles = createStyles(() => ({
  table: {
    "tbody tr": {
      display: "flex",
      marginBottom: "7px",
      paddingBottom: "7px",
      paddingLeft: "10px",
      paddingRight: "15px",
      borderBottom: "0px",
    },
    "tbody tr:first-of-type": {
      paddingTop: "7px",
    },
    "tbody tr:last-of-type": {
      borderBottom: "none",
    },
    "tbody tr th": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: `0 0 ${LEGEND_SYMBOL_WIDTH}px`,
      width: LEGEND_SYMBOL_WIDTH,
      marginRight: "20px",
      padding: 0,
    },
    "tbody tr th img": {
      maxWidth: "100%",
      height: "auto",
    },
    "tbody tr td": {
      display: "flex",
      alignContent: "center",
    },
  },
}));

export const LegendPanel = () => {
  const { state, dispatch } = useOptions();
  const { classes } = useStyles();
  const { t } = useTranslation();
  const CurrentLegendBody = LEGENDS[state.mapType];
  // The legend body (code and ~100 symbol images) is loaded only once the panel is opened for the first time,
  // then it stays rendered so the closing animation isn't cut short
  const [wasOpened, setWasOpened] = useState(state.isLegendOpen);
  if (state.isLegendOpen && !wasOpened) {
    setWasOpened(true);
  }

  const closeLegendPanel = useCallback(() => {
    dispatch(updateNonSaveableOptions({ isLegendOpen: false }));
  }, [dispatch]);

  useEffect(() => {
    if (!CurrentLegendBody) closeLegendPanel();
  }, [CurrentLegendBody, closeLegendPanel, state.mapType]);

  return CurrentLegendBody ? (
    <Stack
      gap={4}
      sx={{
        position: "relative",
        width: PANEL_WIDTH,
        height: "100%",
        marginRight: state.isLegendOpen ? 0 : -PANEL_WIDTH,
        transition: `margin ${PANEL_TOGGLE_ANIMATION_DURATION}s`,
        flexShrink: 0,
        zIndex: 100,
      }}
    >
      <Paper
        shadow="lg"
        p={0}
        radius={0}
        sx={(theme, u) => ({
          [u.dark]: {
            backgroundColor:
              state.mapType === MapID.tron ? "#364D6C" : theme.colors.dark[8],
          },
          [u.light]: {
            backgroundColor: state.mapType === MapID.tron ? "#364D6C" : "white",
          },
          height: "100%",
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          zIndex: 10,
          backgroundColor: state.mapType === MapID.tron ? "#364D6C" : "white",
          color: state.mapType === MapID.tron ? "white" : "inherit",
        })}
      >
        {/* The table scrolls in the space left under the title. A fixed max height (e.g. 96vh)
            overflowed the panel on mobile, where vh includes the browser toolbars, and cut off the last rows */}
        <Box
          pl="md"
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Title order={3} sx={{ paddingTop: "5px", paddingBottom: "5px" }}>
            {t("map_controls.legend_button_tooltip")}
          </Title>
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            <Table
              className={classes.table}
              sx={{ color: state.mapType === MapID.tron ? "white" : "inherit" }}
            >
              {wasOpened ? (
                <Suspense fallback={null}>
                  <CurrentLegendBody />
                </Suspense>
              ) : null}
            </Table>
          </div>
        </Box>
        <CloseButton
          sx={(theme, u) => ({
            [u.dark]: {
              color:
                state.mapType === MapID.tron
                  ? theme.colors.gray[1]
                  : theme.colors.gray[4],
            },
            [u.light]: {
              color:
                state.mapType === MapID.tron
                  ? theme.colors.gray[1]
                  : theme.colors.dark[7],
            },
            position: "absolute",
            top: 6,
            right: 6,
          })}
          onClick={closeLegendPanel}
          variant="transparent"
        />
      </Paper>
    </Stack>
  ) : null;
};
